import React, { useEffect, useState } from 'react';

/**
 * Props interface for CountdownTimer component.
 *
 * @param targetDate - The date to count down to.
 */
interface Props {
  targetDate: Date;
}

/**
 * calculateTimeLeft function calculates the time left until the target date.
 *
 * @param targetDate - The date to count down to.
 * @returns The time left until the target date in days, hours, minutes, and seconds.
 */
function calculateTimeLeft(targetDate: Date) {
  const now = new Date();
  const difference = targetDate.getTime() - now.getTime();

  if (difference <= 0) return null;

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((difference / (1000 * 60)) % 60);
  const seconds = Math.floor((difference / 1000) % 60);

  return { days, hours, minutes, seconds };
}

/**
 * CountdownTimer component displays a countdown timer to a target date.
 *
 * @param param0 - props for CountdownTimer component.
 * @returns A countdown timer that displays the time left until the target date.
 */
const CountdownTimer: React.FC<Props> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (!timeLeft) return <p>The event has started!</p>;

  return (
    <p>
      ⏳ {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s remaining
    </p>
  );
};

export default CountdownTimer;
